import { useSelector } from "react-redux";
import { selectSelectedTake3D } from "../../../app/slices/TrainingSlice";
import { selectEnvironmentInteractionPoints } from "../../../app/slices/EnvironmentSlice";
import { ReactNode, useMemo } from "react";
import PiCreatorDropdown from "../../../components/PiCreatorDropdown/PiCreatorDropdown";
import useSituationHelper from "../../../hooks/SituationHelper.hook";
import {
  getEnvironmentPositionContexts,
  getSituationPositionId,
} from "../EnvironmentPositionContext";

export default function NodePositionDropdown(props: {
  label: string;
  selectedPositionId: string;
  onClick: (positionId: string) => void;
  accessory?: ReactNode;
  helperText?: ReactNode;
}) {
  const { onClick, selectedPositionId } = props;
  const currentTakeLocation = useSelector(selectSelectedTake3D);
  const allPositions = useSelector(selectEnvironmentInteractionPoints);
  const { getSituationMetadata } = useSituationHelper();

  const contexts = useMemo(() => getEnvironmentPositionContexts(
    allPositions ?? [],
    currentTakeLocation?.situations,
    getSituationMetadata,
  ), [allPositions, currentTakeLocation?.situations, getSituationMetadata]);
  const currentLocationId = currentTakeLocation?.interactionPointId;
  const options = contexts.flatMap((context) => context.actorPositions.map((position) => ({
    id: getSituationPositionId(context, position.id),
    sourceId: position.id,
    name: position.name,
    context,
  })));
  const selected = options.find((position) => position.id === selectedPositionId)
    ?? options.find((position) => position.sourceId === selectedPositionId);
  const locationName = (context: typeof contexts[number]) => context.takePositionName || context.takePositionId;
  const selectedLabel = selected
    ? selected.context.takePositionId === currentLocationId
      ? selected.name
      : locationName(selected.context) + " \u00b7 " + selected.name
    : "";
  const groups = [...contexts]
    .filter((context) => context.actorPositions.length > 0)
    .sort((a, b) => {
      if (a.takePositionId === currentLocationId) return -1;
      if (b.takePositionId === currentLocationId) return 1;
      return locationName(a).localeCompare(locationName(b));
    })
    .map((context) => ({
      id: context.takePositionId,
      label: context.takePositionId === currentLocationId
        ? "Current Take \u00b7 " + locationName(context)
        : locationName(context),
      items: context.actorPositions.map((position) => ({
        id: getSituationPositionId(context, position.id),
        label: position.name,
        onSelect: () => onClick(getSituationPositionId(context, position.id)),
      })),
    }));
  const positionDropdown = (
    <PiCreatorDropdown
      style="square"
      trigger={<div title={selectedLabel} className="flex items-center h-full px-[8px] text-creator-text text-[13px]">
        <span className="truncate">{selectedLabel}</span>
      </div>}
      groups={groups}
    />
  );

  return (
    <div className="flex flex-col gap-[8px] noDrag nowheel">
      <div className="font-medium text-creator-text-sub text-[14px]">
        {props.label}
      </div>
      <div className="flex items-center gap-[8px]">
        <div className="flex-1 min-w-0">{positionDropdown}</div>
        {props.accessory && <div className="shrink-0">{props.accessory}</div>}
      </div>
      {props.helperText && (
        <div className="text-[12px] leading-[16px] text-creator-text-sub">
          {props.helperText}
        </div>
      )}
    </div>
  );
}
